import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Space,
  Badge,
  Typography,
  Descriptions,
  Row,
  Col,
  Tabs
} from 'antd';
import {
  EditOutlined,
  ReloadOutlined,
  EyeOutlined,
  KeyOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import './index.less';
import saveAs from 'file-saver';
import dayjs from 'dayjs';

const { Option } = Select;
const { Title, Text } = Typography;
const { TabPane } = Tabs;

interface UmaCard {
  id: number;
  name: string;
  name_en: string;
  rarity: string;
  series: string;
  has_key: boolean;
  hash?: string;
  dynamicHashes?: DynamicHash[];
  exchangeRecords?: ExchangeRecord[];
}

interface DynamicHash {
  id: number;
  hash: string;
  is_used: boolean;
  created: string;
  used_at?: string;
}

interface ExchangeRecord {
  id: number;
  hash: string;
  keys_used: number;
  shipping_address: string;
  exchange_type: string;
  status: string;
  created: string;
  processed_at?: string;
  shipping_info?: string;
}

interface ProbabilityConfig {
  id: number;
  config_key: string;
  probability: number;
  description: string;
  created: string;
  updated: string;
}

const UmaCardManager: React.FC = () => {
  const [cards, setCards] = useState<UmaCard[]>([]);
  const [exchangeRecords, setExchangeRecords] = useState<ExchangeRecord[]>([]);
  const [probabilityConfigs, setProbabilityConfigs] = useState<ProbabilityConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [generateModalVisible, setGenerateModalVisible] = useState(false);
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [probabilityModalVisible, setProbabilityModalVisible] = useState(false);
  const [selectedCard, setSelectedCard] = useState<UmaCard | null>(null);
  const [selectedRecord, setSelectedRecord] = useState<ExchangeRecord | null>(null);
  const [selectedConfig, setSelectedConfig] = useState<ProbabilityConfig | null>(null);
  const [form] = Form.useForm();
  const [updateForm] = Form.useForm();
  const [probabilityForm] = Form.useForm();

  useEffect(() => {
    fetchCards();
    fetchExchangeRecords();
    fetchProbabilityConfigs();
  }, []);

  const fetchCards = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/uma/cards?limit=100');
      const data = await response.json();
      setCards(data.cards || data);
    } catch (error) {
      message.error('获取卡牌数据失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchExchangeRecords = async () => {
    try {
      // 获取所有兑换记录
      const response = await fetch('/api/v1/uma/exchange/records');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log('Exchange records response:', data);

      // 确保数据格式正确
      if (data && typeof data === 'object') {
        const records = data.records || data || [];
        if (Array.isArray(records)) {
          setExchangeRecords(records);
        } else {
          console.warn('Exchange records data is not an array:', records);
          setExchangeRecords([]);
        }
      } else {
        console.warn('Invalid exchange records response:', data);
        setExchangeRecords([]);
      }
    } catch (error) {
      console.error('Error fetching exchange records:', error);
      message.error('获取兑换记录失败: ' + (error instanceof Error ? error.message : String(error)));
      setExchangeRecords([]);
    }
  };

  const fetchProbabilityConfigs = async () => {
    try {
      const response = await fetch('/api/v1/uma/probability-configs');
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      // 检查响应内容类型
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await response.text();
        console.error('Non-JSON response:', textResponse);
        throw new Error('API返回非JSON格式响应');
      }

      const data = await response.json();
      console.log('Probability configs response:', data);

      // 确保数据格式正确
      if (Array.isArray(data)) {
        setProbabilityConfigs(data);
      } else {
        console.warn('Probability configs data is not an array:', data);
        setProbabilityConfigs([]);
      }
    } catch (error) {
      console.error('Error fetching probability configs:', error);
      message.error('获取概率配置失败: ' + (error instanceof Error ? error.message : String(error)));
      setProbabilityConfigs([]);
    }
  };

  const initializeProbabilityConfigs = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/uma/probability-configs/initialize', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        message.success('概率配置初始化成功');
        fetchProbabilityConfigs();
      } else {
        const errorText = await response.text();
        console.error('Initialize error:', errorText);
        message.error('概率配置初始化失败: ' + response.status);
      }
    } catch (error) {
      console.error('Error initializing probability configs:', error);
      message.error('概率配置初始化失败: ' + (error instanceof Error ? error.message : String(error)));
    } finally {
      setLoading(false);
    }
  };

  const updateProbabilityConfig = async (values: any) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/v1/uma/probability-configs/${selectedConfig?.config_key}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          probability: values.probability,
          description: values.description
        })
      });

      if (response.ok) {
        message.success('概率配置更新成功');
        setProbabilityModalVisible(false);
        probabilityForm.resetFields();
        fetchProbabilityConfigs();
      } else {
        const errorText = await response.text();
        console.error('Update error:', errorText);
        message.error('概率配置更新失败: ' + response.status);
      }
    } catch (error) {
      console.error('Error updating probability config:', error);
      message.error('概率配置更新失败: ' + (error instanceof Error ? error.message : String(error)));
    } finally {
      setLoading(false);
    }
  };

  const generateDynamicHash = async (values: any) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/v1/uma/generate-hash/${values.cardId}/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          quantity: values.quantity
        })
      });

      if (response.ok) {
        const data = await response.json();
        message.success(`成功生成 ${values.quantity} 个动态hash`);

        // 导出Excel
        if (data.hashes && data.hashes.length > 0) {
          exportHashesToExcel(data.hashes, values.cardId);
        }

        setGenerateModalVisible(false);
        form.resetFields();
        fetchCards();
      } else {
        message.error('生成动态hash失败');
      }
    } catch (error) {
      message.error('生成动态hash失败');
    } finally {
      setLoading(false);
    }
  };

  const exportHashesToExcel = (hashes: string[], cardId: number) => {
    const card = cards.find(c => c.id === cardId);
    const cardName = card ? card.name_en : 'Unknown';
    const timestamp = dayjs().format('MM/DD');

    // const data = hashes.map((hash, index) => ({
    //   '序号': index + 1,
    //   '卡牌ID': cardId,
    //   '卡牌名称': cardName,
    //   '动态Hash': hash,
    //   '二维码URL': `https://uma.pixelhouse.tools/uma/${hash}`,
    //   '生成时间': new Date().toLocaleString()
    // }));

    const name = cardName;
    const fileName = `${name}_${timestamp}.txt`;

    // 提取所有 hash 值并用换行符连接，最后添加一个换行符
    const content = hashes.map(item => `https://ds2.cn/u/${item}`).join('\r\n') + '\r\n';
    // 添加 UTF-8 BOM 标记
    const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
    const blob = new Blob([BOM, content], { type: 'text/plain;charset=utf-8' });
    saveAs(blob, fileName);

    // const ws = XLSX.utils.json_to_sheet(data);
    // const wb = XLSX.utils.book_new();
    // XLSX.utils.book_append_sheet(wb, ws, 'UMA动态Hash');
    // XLSX.writeFile(wb, `UMA_${cardName}_动态Hash_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const updateShippingInfo = async (values: any) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/v1/uma/exchange/${selectedRecord?.id}/shipping`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          shipping_info: values.shipping_info,
          status: values.status
        })
      });

      if (response.ok) {
        message.success('物流信息更新成功');
        setUpdateModalVisible(false);
        updateForm.resetFields();
        fetchExchangeRecords();
      } else {
        message.error('物流信息更新失败');
      }
    } catch (error) {
      message.error('物流信息更新失败');
    } finally {
      setLoading(false);
    }
  };

  const cardColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '卡牌名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '英文名',
      dataIndex: 'name_en',
      key: 'name_en',
    },
    {
      title: '稀有度',
      dataIndex: 'rarity',
      key: 'rarity',
      render: (rarity: string) => <Badge status="processing" text={rarity} />,
    },
    {
      title: '系列',
      dataIndex: 'series',
      key: 'series',
    },
    {
      title: '支持密钥',
      dataIndex: 'has_key',
      key: 'has_key',
      render: (hasKey: boolean) => (
        <Badge status={hasKey ? 'success' : 'default'} text={hasKey ? '是' : '否'} />
      ),
    },
    {
      title: '动态Hash数量',
      key: 'dynamic_hashes',
      render: (record: UmaCard) => (
        <Text>{record.dynamicHashes?.length || 0}</Text>
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (record: UmaCard) => (
        <Space size="middle">
          {record.has_key && (
            <Button
              type="primary"
              icon={<KeyOutlined />}
              onClick={() => {
                setSelectedCard(record);
                setGenerateModalVisible(true);
              }}
            >
              生成Hash
            </Button>
          )}
          <Button
            icon={<EyeOutlined />}
            onClick={() => {
              Modal.info({
                title: '卡牌详情',
                width: 600,
                content: (
                  <Descriptions column={2} size="small">
                    <Descriptions.Item label="ID">{record.id}</Descriptions.Item>
                    <Descriptions.Item label="中文名">{record.name}</Descriptions.Item>
                    <Descriptions.Item label="英文名">{record.name_en}</Descriptions.Item>
                    <Descriptions.Item label="稀有度">{record.rarity}</Descriptions.Item>
                    <Descriptions.Item label="系列">{record.series}</Descriptions.Item>
                    <Descriptions.Item label="支持密钥">{record.has_key ? '是' : '否'}</Descriptions.Item>
                    <Descriptions.Item label="静态Hash">{record.hash || '无'}</Descriptions.Item>
                    <Descriptions.Item label="动态Hash">{record.dynamicHashes?.length || 0} 个</Descriptions.Item>
                  </Descriptions>
                )
              });
            }}
          >
            查看
          </Button>
        </Space>
      ),
    },
  ];

  const exchangeColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '动态Hash',
      dataIndex: 'hash',
      key: 'hash',
      render: (hash: string) => <Text code>{hash.substring(0, 10)}...</Text>,
    },
    {
      title: '使用密钥数',
      dataIndex: 'keys_used',
      key: 'keys_used',
    },
    {
      title: '收货地址',
      dataIndex: 'shipping_address',
      key: 'shipping_address',
      render: (address: string) => (
        <Text ellipsis style={{ maxWidth: 200 }}>{address}</Text>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusConfig = {
          'pending': { color: 'orange', text: '待处理' },
          'processing': { color: 'blue', text: '处理中' },
          'shipped': { color: 'green', text: '已发货' },
          'delivered': { color: 'success', text: '已送达' },
          'cancelled': { color: 'red', text: '已取消' }
        };
        const config = statusConfig[status as keyof typeof statusConfig] || { color: 'default', text: status };
        return <Badge status={config.color as any} text={config.text} />;
      },
    },
    {
      title: '兑换时间',
      dataIndex: 'created',
      key: 'created',
      render: (time: string) => time ? new Date(time).toLocaleString() : '-',
    },
    {
      title: '操作',
      key: 'action',
      render: (record: ExchangeRecord) => (
        <Space size="middle">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => {
              setSelectedRecord(record);
              updateForm.setFieldsValue({
                shipping_info: record.shipping_info || '',
                status: record.status
              });
              setUpdateModalVisible(true);
            }}
          >
            更新物流
          </Button>
          <Button
            icon={<EyeOutlined />}
            onClick={() => {
              Modal.info({
                title: '兑换详情',
                width: 600,
                content: (
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="兑换ID">{record.id}</Descriptions.Item>
                    <Descriptions.Item label="动态Hash">{record.hash}</Descriptions.Item>
                    <Descriptions.Item label="使用密钥数">{record.keys_used}</Descriptions.Item>
                    <Descriptions.Item label="收货地址">{record.shipping_address}</Descriptions.Item>
                    <Descriptions.Item label="兑换类型">{record.exchange_type}</Descriptions.Item>
                    <Descriptions.Item label="状态">{record.status}</Descriptions.Item>
                    <Descriptions.Item label="兑换时间">{new Date(record.created).toLocaleString()}</Descriptions.Item>
                    <Descriptions.Item label="物流信息">{record.shipping_info || '暂无'}</Descriptions.Item>
                  </Descriptions>
                )
              });
            }}
          >
            查看详情
          </Button>
        </Space>
      ),
    },
  ];

  const probabilityColumns = [
    {
      title: '配置项',
      dataIndex: 'config_key',
      key: 'config_key',
      render: (key: string) => {
        const keyNames = {
          'key_drop_rate': '兑换密钥掉落概率',
          'seed_min_amount': '种子最小数量',
          'seed_max_amount': '种子最大数量'
        };
        return keyNames[key as keyof typeof keyNames] || key;
      },
    },
    {
      title: '当前值',
      dataIndex: 'probability',
      key: 'probability',
      render: (probability: number, record: ProbabilityConfig) => {
        if (!probability && probability !== 0) return '-';
        if (record && record.config_key === 'key_drop_rate') {
          return `${(probability * 100).toFixed(2)}%`;
        }
        return probability.toString();
      },
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: '最后更新',
      dataIndex: 'updated',
      key: 'updated',
      render: (time: string) => time ? new Date(time).toLocaleString() : '-',
    },
    {
      title: '操作',
      key: 'action',
      render: (record: ProbabilityConfig) => (
        <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={() => {
            setSelectedConfig(record);
            probabilityForm.setFieldsValue({
              probability: record.probability,
              description: record.description
            });
            setProbabilityModalVisible(true);
          }}
        >
          编辑
        </Button>
      ),
    },
  ];

  return (
    <div className="uma-card-manager">
      <Title level={2}>UMA卡牌管理</Title>

      <Tabs defaultActiveKey="cards">
        <TabPane tab="卡牌管理" key="cards">
          <Card>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col>
                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  onClick={fetchCards}
                  loading={loading}
                >
                  刷新数据
                </Button>
              </Col>
            </Row>

            <Table
              dataSource={cards}
              columns={cardColumns}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
              scroll={{ x: true }}
            />
          </Card>
        </TabPane>

        <TabPane tab="兑换记录" key="exchange">
          <Card>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col>
                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  onClick={fetchExchangeRecords}
                  loading={loading}
                >
                  刷新记录
                </Button>
              </Col>
            </Row>

            <Table
              dataSource={exchangeRecords || []}
              columns={exchangeColumns}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
              scroll={{ x: true }}
            />
          </Card>
        </TabPane>

        <TabPane tab="概率配置" key="probability">
          <Card>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col>
                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  onClick={fetchProbabilityConfigs}
                  loading={loading}
                >
                  刷新配置
                </Button>
              </Col>
              <Col>
                <Button
                  type="default"
                  onClick={initializeProbabilityConfigs}
                  loading={loading}
                >
                  初始化配置
                </Button>
              </Col>
            </Row>

            <Table
              dataSource={probabilityConfigs || []}
              columns={probabilityColumns}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
              scroll={{ x: true }}
            />
          </Card>
        </TabPane>
      </Tabs>

      {/* 生成动态Hash模态框 */}
      <Modal
        title={`生成动态Hash - ${selectedCard?.name}`}
        open={generateModalVisible}
        onCancel={() => {
          setGenerateModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={generateDynamicHash}
        >
          <Form.Item
            name="cardId"
            label="卡牌ID"
            initialValue={selectedCard?.id}
          >
            <Input disabled />
          </Form.Item>

          <Form.Item
            name="quantity"
            label="生成数量"
            rules={[{ required: true, message: '请输入生成数量' }]}
          >
            <InputNumber min={1} max={1000} placeholder="请输入生成数量" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
              >
                生成并导出
              </Button>
              <Button
                onClick={() => {
                  setGenerateModalVisible(false);
                  form.resetFields();
                }}
              >
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 更新物流信息模态框 */}
      <Modal
        title="更新物流信息"
        open={updateModalVisible}
        onCancel={() => {
          setUpdateModalVisible(false);
          updateForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={updateForm}
          layout="vertical"
          onFinish={updateShippingInfo}
        >
          <Form.Item
            name="status"
            label="订单状态"
            rules={[{ required: true, message: '请选择订单状态' }]}
          >
            <Select>
              <Option value="pending">待处理</Option>
              <Option value="processing">处理中</Option>
              <Option value="shipped">已发货</Option>
              <Option value="delivered">已送达</Option>
              <Option value="cancelled">已取消</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="shipping_info"
            label="物流信息"
            rules={[{ required: true, message: '请输入物流信息' }]}
          >
            <Input.TextArea
              rows={4}
              placeholder="请输入物流公司、快递单号等信息"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
              >
                更新
              </Button>
              <Button
                onClick={() => {
                  setUpdateModalVisible(false);
                  updateForm.resetFields();
                }}
              >
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 概率配置编辑模态框 */}
      <Modal
        title={`编辑概率配置 - ${selectedConfig?.config_key}`}
        open={probabilityModalVisible}
        onCancel={() => {
          setProbabilityModalVisible(false);
          probabilityForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={probabilityForm}
          layout="vertical"
          onFinish={updateProbabilityConfig}
        >
          <Form.Item
            name="probability"
            label={selectedConfig?.config_key === 'key_drop_rate' ? '概率 (输入0-1之间的小数，如0.01表示1%)' : '数值'}
            rules={[{ required: true, message: '请输入数值' }]}
          >
            <InputNumber
              min={0}
              max={selectedConfig?.config_key === 'key_drop_rate' ? 1 : 100}
              step={selectedConfig?.config_key === 'key_drop_rate' ? 0.01 : 1}
              style={{ width: '100%' }}
              placeholder={selectedConfig?.config_key === 'key_drop_rate' ? '请输入概率 (0-1)' : '请输入数值'}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="描述"
            rules={[{ required: true, message: '请输入描述' }]}
          >
            <Input.TextArea
              rows={3}
              placeholder="请输入配置项的描述"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
              >
                保存
              </Button>
              <Button
                onClick={() => {
                  setProbabilityModalVisible(false);
                  probabilityForm.resetFields();
                }}
              >
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UmaCardManager;